<script>
import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'ProviderSwitchModal',
  data() {
    return {
      associatedRolesLoading: true,
      associatedProviderLoading: false,
      progress: false,
      form: {
        localProviderId: '',
        rolesList: [],
      },
      associatedProvider: null,
      associatedRoles: [],
      rules: {
        localProviderId: [
          {
            required: true,
            message: 'Select the Provider',
            trigger: 'change',
          },
        ],
        rolesList: [
          {
            required: true,
            message: 'Select at least one role',
            trigger: 'change',
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('providers', [
      'providersList',
      'providerSearching',
    ]),
    ...mapGetters('manage', ['productCategories']),
    ...mapGetters('auth', ['isViewSwitched']),
    manageProvidersUrl() {
      const adminProducts = this.productCategories.find(pc => pc.title === 'Admin')
      const superManageProduct = adminProducts && adminProducts.items[0]

      if (superManageProduct) {
        const baseUrl = superManageProduct.product.url

        return {
          listView: `${baseUrl}providers`,
          createView: `${baseUrl}provider/create`,
        }
      }

      return null
    },
    showProviderWarn() {
      return this.form.localProviderId &&
        !this.associatedProvider &&
        !this.providerSearching &&
        !this.associatedProviderLoading
    },
    showRolesWarn() {
      return !this.associatedRolesLoading && !this.associatedRoles.length
    },
    submitDisabled() {
      return this.progress ||
        this.showProviderWarn ||
        this.showRolesWarn
    },
  },
  async created() {
    this.associatedRolesLoading = true

    this.associatedRoles = await this.getCurrentProductRoles()

    this.associatedRolesLoading = false
  },
  methods: {
    ...mapActions('providers', [
      'searchProviders',
    ]),
    ...mapActions('manage', [
      'getAssociatedManageProvider',
      'getCurrentProductRoles',
    ]),
    async onInput(localProviderId) {
      this.associatedProviderLoading = true

      if (process.env.VUE_APP_NAME === 'manage') {
        this.associatedProvider = {
          id: localProviderId,
        }
      } else if (localProviderId) {
        this.associatedProvider = await this.getAssociatedManageProvider(localProviderId)
      } else {
        this.associatedProvider = null
      }

      this.associatedProviderLoading = false
    },
    async onSubmit() {
      const formValid = await this.validate()

      if (!formValid) {
        return false
      }

      this.progress = true

      const loginContext = {
        switch_provider: this.associatedProvider.id,
        switch_roles: this.form.rolesList,
      }

      await this.$auth.loginWithContext(loginContext)

      this.writeSwitchedProviderData(loginContext)

      window.location.replace('/')

      this.$modal.self.hide()

      this.progress = false
    },
    writeSwitchedProviderData(loginContext) {
      const { providersList, associatedRoles } = this
      const { localProviderId, rolesList } = this.form
      const providerData = {
        loginContext,
        switchedProviderName: providersList.find(p => p.value === localProviderId).label,
        switchedRoleNames: rolesList.map(roleName => {
          return associatedRoles.find(ar => ar.name === roleName).friendlyName
        }),
      }

      localStorage.setItem('switchedProviderData', JSON.stringify(providerData))
    },
    validate() {
      return this.$refs.form.validate().catch(() => false)
    },
  },
}
</script>

<template>
  <base-modal title="Switch to Provider View">
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :disabled="progress"
      label-width="180px"
      label-position="top"
    >
      <el-form-item
        label="Select Provider"
        prop="localProviderId"
      >
        <el-select
          v-model="form.localProviderId"
          v-loading="associatedProviderLoading"
          :remote-method="searchProviders"
          :loading="providerSearching"
          loading-text="Searching provider by name..."
          placeholder="Start typing provider name"
          filterable
          remote
          @input="onInput"
        >
          <el-option
            v-for="item in providersList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <transition name="el-fade-in">
        <div
          v-if="showProviderWarn"
          :class="$style.warn"
        >
          This local Provider does not have corresponding Manage Provider.
          <div v-if="manageProvidersUrl">
            <a
              :href="manageProvidersUrl.createView"
              target="_blank"
            >
              Create
            </a>
            a new Manage Provider or
            <a
              :href="manageProvidersUrl.listView"
              target="_blank"
            >
              associate
            </a>
            with an existing one.
          </div>
          <div>
            Use
            <span :class="$style.warnId">{{ form.localProviderId }}</span> as a local Provider ID.
          </div>
        </div>
      </transition>

      <el-form-item
        label="Select Roles"
        prop="rolesList"
      >
        <el-select
          v-model="form.rolesList"
          v-loading="associatedRolesLoading"
          placeholder="Select roles"
          :disabled="associatedRolesLoading || !associatedRoles.length"
          no-data-text="No roles"
          multiple
        >
          <el-option
            v-for="role in associatedRoles"
            :key="role.name"
            :label="role.friendlyName"
            :value="role.name"
          />
        </el-select>
      </el-form-item>
      <transition name="el-fade-in">
        <div
          v-if="showRolesWarn"
          :class="$style.warn"
        >
          Current product doesn't have roles.
        </div>
      </transition>

      <div :class="$style.note">
        <i :class="[$style.noteIcon, 'el-icon-info']" /> Note: Switching to the Provider view will provide you with all the access, permissions and authority associated with the Provider you select and the Role/s you have selected. Any actions taken will therefore be done as if actioned by the Provider.
      </div>

      <div :class="$style.buttons">
        <el-button
          :loading="progress"
          type="primary"
          :class="$style.button"
          :disabled="submitDisabled"
          @click="onSubmit"
        >
          Switch View
        </el-button>
      </div>
    </el-form>
  </base-modal>
</template>

<style lang="scss" module>
.warn {
  margin-top: -1rem;
  margin-bottom: 1rem;
  font-weight: bold;
  color: var(--color-warning);
}

.warnId {
  color: var(--color-text)
}

.note {
  padding: 1rem;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  font-weight: bold;
  word-break: break-word;
  background-color: var(--color-divider);
  border-radius: 1rem;
}

.noteIcon {
  font-size: 1rem;
  color: var(--color-primary);
}

.buttons  {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button  {
  width: 12rem;
}
</style>
